import React, { useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";

// material-ui
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

// project import
import MainCard from "components/MainCard";
import {
  ExclamationCircleFilled,
  InfoCircleOutlined,
  WarningFilled,
} from "@ant-design/icons";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const AlertBlock = ({ data, settingsData }) => {
  const remainingDays =
    data?.remainingDays == null ? null : Number(data.remainingDays);
  const dailyUsage = Number(data?.dailyUsage || 0);
  const timezone = settingsData?.timeConfig?.timezone || "UTC";
  const today = useMemo(() => dayjs().tz(timezone), [timezone]);

  const depletionDate = useMemo(() => {
    if (remainingDays == null || !Number.isFinite(remainingDays)) return null;
    return today.add(Math.max(remainingDays, 0), "day").format("YYYY-MM-DD");
  }, [remainingDays, today]);

  const alert = useMemo(() => {
    if (!data) return null;
    if (dailyUsage <= 0) {
      return {
        title: "Normal",
        severity: "info",
        message:
          "Your organization's credit usage is within the normal range. No immediate action is required.",
      };
    }
    if (data.balance <= 0) {
      return {
        title: "Grace Period",
        severity: "error",
        message:
          "Your credits have been depleted. Your services are currently in a grace period and will be fully suspended on [Date]. Please renew your subscription or purchase credits immediately.",
      };
    }
    if (remainingDays == null || !Number.isFinite(remainingDays)) return null;
    if (remainingDays < 30) {
      return {
        title: "Low Balance",
        severity: "urgent",
        message:
          "Your account is running out of credits and will reach zero on [Date]. Top up immediately to avoid impending service disruption.",
      };
    }
    if (remainingDays <= 90) {
      return {
        title: "Critical Alert",
        severity: "critical",
        message:
          "Your credits are projected to be depleted by [Date]. Please procure additional credits soon to ensure uninterrupted network management services.",
      };
    }
    if (remainingDays <= 365) {
      return {
        title: "Predictive Alert",
        severity: "warning",
        message:
          "Your organization is on track to consume all credits in approximately 365 days. Consider purchasing additional credits or adjusting service subscriptions.",
      };
    }
    return {
      title: "Normal",
      severity: "info",
      message:
        "Your organization's credit usage is within the normal range. No immediate action is required.",
    };
  }, [data, dailyUsage, remainingDays]);

  if (!alert) return null;

  const styles = {
    info: { color: "#262626", iconColor: "#262626", background: "#FFFFFF", border: "#D9D9D9" },
    warning: { color: "#D48806", iconColor: "#D48806", background: "#FFFBE6", border: "#FFE58F" },
    critical: { color: "#D46B08", iconColor: "#D46B08", background: "#FFF8E6", border: "#FFE58F" },
    urgent: { color: "#CF1322", iconColor: "#CF1322", background: "#FFF1F0", border: "#FFA39E" },
    error: { color: "#CF1322", iconColor: "#CF1322", background: "#FFF1F0", border: "#FFA39E" },
  }[alert.severity];
  const Icon =
    alert.severity === "info"
      ? InfoCircleOutlined
      : alert.severity === "error"
        ? ExclamationCircleFilled
        : WarningFilled;
  const message = alert.message.replace(
    "[Date]",
    alert.severity === "error"
      ? today.add(3, "day").format("YYYY-MM-DD")
      : depletionDate || "<Date>",
  );

  return (
    <MainCard
      sx={{
        bgcolor: styles.background,
        border: `1px solid ${styles.border}`,
        borderRadius: "8px",
        boxShadow: "none",
      }}
      contentSX={{ p: 2 }}
    >
      <Stack direction='row' spacing={1.5} alignItems='flex-start'>
        <Icon
          style={{ color: styles.iconColor, fontSize: "16px", marginTop: "2px" }}
        />
        <Stack spacing={0.5}>
          <Typography
            variant='subtitle2'
            sx={{
              color: styles.color,
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >
            {alert.title}
          </Typography>
          <Typography
            variant='body2'
            sx={{
              color: "rgba(0, 0, 0, 0.65)",
              fontSize: "13px",
              lineHeight: 1.5,
            }}
          >
            {message}
          </Typography>
        </Stack>
      </Stack>
    </MainCard>
  );
};

export default AlertBlock;
